import { Routes } from "@angular/router";
import { AuthGuard } from "./common/auth.guard";
import { Level } from "./shared/enums/level";
import { phoneBlockedGuard } from "./shared/mobile/mobile-access";
import { unsavedChangesGuard } from "./shared/unsaved-changes/unsaved-changes.guard";

export const APP_ROUTES: Routes = [
  {
    path: '',
    canActivate: [AuthGuard],
    loadComponent: () => import('./shell/shell.component').then(m => m.ShellComponent),
    children: [
      {
        path: '',
        data: {
          navLabel: 'Dashboard',
          showIcon: false,
          iconPath: 'Home',
          level: [Level.USER, Level.ADMIN],
          showable: true
        },
        loadComponent: () => import('./dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'reports',
        data: {
          navLabel: 'Rapporte',
          showIcon: false,
          iconPath: 'FileBarChart2',
          level: [Level.USER, Level.ADMIN],
          showable: true
        },
        loadComponent: () => import('./report/report.component').then(m => m.ReportComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./report/list/list.component').then(m => m.ListComponent),
          },
          {
            path: ':id',
            data: {
              showBackButton: true,
              backRoute: '/reports'
            },
            loadComponent: () => import('./report/detail/detail.component').then(m => m.DetailComponent),
          }
        ]
      },
      {
        path: 'conflicts',
        data: {
          navLabel: 'Konflikte',
          showIcon: false,
          iconPath: 'AlertTriangle',
          level: [Level.USER, Level.ADMIN],
          showable: false // Rendered as a fixed entry at the bottom of the sidebar (above the profile), not in the main list
        },
        loadComponent: () => import('./conflict-resolution/conflict-resolution.component').then(m => m.ConflictResolutionComponent),
      },
      {
        path: 'systems',
        data: {
          // Die Route heißt weiter `systems`; geführt wird darunter das **Objekt**
          // mit seinen Anlagen. Umbenannt beim Redesign des Moduls (REDESIGN.md).
          navLabel: 'Objekte',
          showIcon: false,
          iconPath: 'Building2',
          level: [Level.USER, Level.ADMIN],
          showable: true
        },
        loadComponent: () => import('./systems/systems.component').then(m => m.SystemsComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./systems/list/list.component').then(m => m.ListComponent),
          },
          {
            // Muss **vor** `:id` stehen, sonst schluckt die ID-Route den Pfad.
            // Die zweite Ansicht des Moduls: alles in einem Raster wie in der
            // Excel der Sachbearbeitung, mit Bearbeiten in der Zelle. Verwaltung,
            // also nicht auf dem Telefon (siehe „Die App auf dem Telefon").
            path: 'table',
            data: {
              showBackButton: true,
              backRoute: '/systems',
              mobile: false
            },
            canActivate: [phoneBlockedGuard],
            loadComponent: () => import('./systems/table/table.component').then(m => m.TableComponent),
          },
          {
            path: ':id',
            data: {
              showBackButton: true,
              backRoute: '/systems'
            },
            loadComponent: () => import('./systems/detail/detail.component').then(m => m.DetailComponent),
          }
        ]
      },
      {
        // Die Wartungen des laufenden Monats: die Vorlage, nach der die
        // Sachbearbeitung die Papierordner für die Monteure rauslegt. Verwaltung,
        // also nicht auf dem Telefon (siehe „Die App auf dem Telefon").
        path: 'maintenances',
        data: {
          navLabel: 'Wartungen',
          showIcon: false,
          iconPath: 'ClipboardCheck',
          level: [Level.USER, Level.ADMIN],
          showable: true,
          mobile: false
        },
        canActivate: [phoneBlockedGuard],
        loadComponent: () => import('./maintenances/maintenances.component').then(m => m.MaintenancesComponent),
      },
      {
        path: 'text-modules',
        data: {
          navLabel: 'Textbausteine',
          showIcon: false,
          iconPath: 'Puzzle',
          level: [Level.USER, Level.ADMIN],
          showable: true,
          // Verwaltung: am Rechner oder Tablet, nicht im Feld (REDESIGN.md).
          mobile: false
        },
        canActivate: [phoneBlockedGuard],
        loadComponent: () => import('./text-modules/text-modules.component').then(m => m.TextModulesComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./text-modules/list/list.component').then(m => m.ListComponent),
          },
          {
            path: ':id',
            data: {
              showBackButton: true,
              backRoute: '/text-modules'
            },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () => import('./text-modules/detail/detail.component').then(m => m.DetailComponent),
          }
        ]
      },
      {
        path: 'users',
        data: {
          navLabel: 'Benutzer',
          showIcon: false,
          iconPath: 'Users2',
          level: [Level.ADMIN],
          showable: true,
          mobile: false
        },
        canActivate: [phoneBlockedGuard],
        loadComponent: () => import('./users/users.component').then(m => m.UsersComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./users/list/list.component').then(m => m.ListComponent),
          },
          {
            path: ':id',
            data: {
              showBackButton: true,
              backRoute: '/users'
            },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () => import('./users/detail/detail.component').then(m => m.DetailComponent),
          }
        ]
      },
      {
        path: 'customers',
        data: {
          navLabel: 'Kunden',
          showIcon: false,
          iconPath: 'Contact',
          level: [Level.ADMIN],
          showable: true
        },
        loadComponent: () => import('./customer/customer.component').then(m => m.CustomerComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./customer/list/list.component').then(m => m.ListComponent),
          },
          {
            path: ':id',
            data: {
              showBackButton: true,
              backRoute: '/customers'
            },
            loadComponent: () => import('./customer/detail/detail.component').then(m => m.DetailComponent),
          }
        ]
      },
      {
        path: 'documents',
        data: {
          navLabel: 'Dokumente',
          showIcon: false,
          iconPath: 'Files',
          level: [Level.USER, Level.ADMIN],
          showable: true
        },
        loadComponent: () => import('./documents/documents.component').then(m => m.DocumentsComponent),
      },
      {
        path: 'billing',
        data: {
          navLabel: 'Abrechnung',
          showIcon: false,
          iconPath: 'Coins',
          level: [Level.ADMIN],
          showable: true,
          mobile: false
        },
        canActivate: [phoneBlockedGuard],
        loadComponent: () => import('./report-billing/report-billing.component').then(m => m.ReportBillingComponent),
      },
      {
        path: 'folder-templates',
        data: {
          navLabel: 'Ordnervorlagen',
          showIcon: false,
          iconPath: 'FolderTree',
          level: [Level.ADMIN],
          showable: true,
          mobile: false
        },
        canActivate: [phoneBlockedGuard],
        loadComponent: () => import('./folder-templates/folder-templates.component').then(m => m.FolderTemplatesComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./folder-templates/list/list.component').then(m => m.ListComponent),
          },
          {
            path: ':id',
            data: {
              showBackButton: true,
              backRoute: '/folder-templates'
            },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () => import('./folder-templates/detail/detail.component').then(m => m.DetailComponent),
          }
        ]
      },
      {
        path: 'folder-assignment-rules',
        data: {
          navLabel: 'Ordner-Zuweisung',
          showIcon: false,
          iconPath: 'FolderOpen',
          level: [Level.ADMIN],
          showable: true,
          mobile: false
        },
        canActivate: [phoneBlockedGuard],
        loadComponent: () => import('./folder-assignment-rules/folder-assignment-rules.component').then(m => m.FolderAssignmentRulesComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./folder-assignment-rules/list/list.component').then(m => m.ListComponent),
          },
          {
            path: ':id',
            data: {
              showBackButton: true,
              backRoute: '/folder-assignment-rules'
            },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () => import('./folder-assignment-rules/detail/detail.component').then(m => m.DetailComponent),
          }
        ]
      },
      {
        path: 'profile',
        data: {
          navLabel: 'Profil',
          showIcon: false,
          iconPath: 'User',
          level: [Level.USER, Level.ADMIN],
          // Hängt am Rail-Fuß, nicht in der Liste — deshalb nicht `showable`.
          showable: false
        },
        // Auch auf dem Telefon erreichbar: Abmelden und die Offline-Daten
        // gehören dorthin, wo gearbeitet wird.
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./profile/profile.component').then(m => m.ProfileComponent),
      },
    ],
  },
  {
    path: 'prototype-checkbox',
    loadComponent: () => import('./prototype/checkbox-probe.component').then(m => m.CheckboxProbeComponent),
  },
  {
    // PROTOTYPE — Wegwerf-Route für Design-Varianten. Vor Release entfernen.
    path: 'prototype',
    loadComponent: () => import('./prototype/prototype.component').then(m => m.PrototypeComponent),
  },
  {
    path: 'auth/login',
    loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: '**',
    loadComponent: () => import('./shared/components/not-found/not-found.component').then(m => m.NotFoundComponent),
  }
];
